/**
 * Link-in-Bio Landing Page — getbitsy.ai
 *
 * Target audience: independent hoteliers visiting from LinkedIn.
 * Goal: convert them into Genesis 108 partners.
 *
 * Aesthetic: high-tech "Infrastructure" — terminal fonts (Chivo Mono),
 * markdown-style # / ## headers, file-tree section labels, amber accents
 * on charcoal. NOT a typical marketing page — it reads like a README.
 *
 * Sections:
 *   1. Hero — "Chief of Staff for independent hotels"
 *   2. Live protocol ticker (real-time from /api/public/protocol-stats)
 *   3. ./calc/context-reload — interactive cost-of-context-loss calculator
 *   4. ./quiz/sovereignty-score — 7-question independence audit
 *   5. ./memory/agent_kb — preview cards, link to /admin/agent-kb
 *   6. ./verify/hotel/* — 3 live public trust cards
 *   7. ./genesis-108 — final CTA + seats remaining
 *
 * Route: /bio  (also /genesis-108)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sparkles, Terminal, FileText, ShieldCheck, ExternalLink,
  ArrowRight, Calculator, Database, Hash, Lock, Download,
  Circle, ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const TOTAL_GENESIS_SEATS = 108;

// ─── Hero copy ───────────────────────────────────────────────────────────
const HERO_LINES = [
  '> Not a chatbot. Not another PMS.',
  '> A Chief of Staff that runs your hotel — and never loses context.',
];

// ─── Sovereignty quiz ────────────────────────────────────────────────────
const SOV_QUESTIONS = [
  { id: 'q1', text: 'Do you own your guest data outright (no Booking/Expedia gatekeeping)?' },
  { id: 'q2', text: 'Can a guest book you direct without an OTA taking 15-25%?' },
  { id: 'q3', text: 'Do you have a loyalty layer that follows the guest across hotels?' },
  { id: 'q4', text: "Is your front-desk AI yours — not Mews' / Cloudbeds' chatbot?" },
  { id: 'q5', text: 'Can a partner verify you exist without phoning a sales rep?' },
  { id: 'q6', text: 'Do you have cryptographic proof your housekeeping actually happened?' },
  { id: 'q7', text: 'Does your staff context (notes, prefs, SOPs) survive turnover?' },
];

function sovTier(score) {
  if (score >= 85) return { label: 'sovereign', color: 'emerald', blurb: "You already think like a protocol. Genesis 108 was designed for you — let's talk." };
  if (score >= 55) return { label: 'partial',   color: 'amber',   blurb: "You've clawed back some control. BHERMES gives you the on-chain proof to monetise it." };
  if (score >= 30) return { label: 'dependent', color: 'orange',  blurb: "You're renting your business from middlemen. The 5%+1% model puts you back in charge." };
  return                  { label: 'captured',  color: 'rose',    blurb: 'OTAs / SaaS own your house. The exit is in this page — keep scrolling.' };
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function BioLandingPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [memvid, setMemvid] = useState(null);

  // calc state
  const [rooms, setRooms] = useState(30);
  const [turnoverPct, setTurnoverPct] = useState(40);   // % staff turnover/yr
  const [otaCount, setOtaCount] = useState(4);

  // quiz state
  const [answers, setAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  // ───── load live ticker + 3 verify cards + memvid stats ─────
  useEffect(() => {
    fetch(`${API}/api/public/protocol-stats`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/api/hotels/public/list`).then(r => r.json()).then((d) => {
      const list = Array.isArray(d) ? d : (d?.hotels || []);
      // Prefer verified hotels with hero photo
      const verified = list.filter(h => h.verification_status === 'verified').slice(0, 3);
      setHotels(verified.length ? verified : list.slice(0, 3));
    }).catch(() => {});
    fetch(`${API}/api/metaverse/bitsy/memory-stats`).then(r => r.json()).then(setMemvid).catch(() => {});
  }, []);

  // ───── context-reload calc ─────
  const calc = useMemo(() => {
    // Each turnover event = ~24h of onboarding + context transfer
    const annualTurnover = Math.round((turnoverPct / 100) * Math.max(rooms * 0.4, 4));
    const turnoverHours = annualTurnover * 24;
    // Each OTA renegotiation/re-onboarding = ~16h/yr/OTA
    const otaHours = otaCount * 16;
    // Per-room note-rebuilding when staff churn = 0.5h × rooms × turnover events
    const noteHours = Math.round(rooms * 0.5 * annualTurnover * 0.6);
    const totalHours = turnoverHours + otaHours + noteHours;
    const dollarPerHour = 55;
    const totalDollar = totalHours * dollarPerHour;
    // BHERMES Agent KB recovers ~70% of staff-context loss + 50% of OTA loss
    const recovered = Math.round(turnoverHours * 0.7 + otaHours * 0.5 + noteHours * 0.8);
    const recoveredDollar = recovered * dollarPerHour;
    return { totalHours, totalDollar, recovered, recoveredDollar, annualTurnover };
  }, [rooms, turnoverPct, otaCount]);

  // ───── quiz scoring ─────
  const quizScore = useMemo(() => {
    const yesCount = Object.values(answers).filter(v => v === 'yes').length;
    return Math.round((yesCount / SOV_QUESTIONS.length) * 100);
  }, [answers]);

  const submitQuiz = useCallback(() => {
    if (Object.keys(answers).length < SOV_QUESTIONS.length) return;
    setQuizSubmitted(true);
  }, [answers]);

  const tier = sovTier(quizScore);
  const seatsRemaining = stats ? Math.max(0, TOTAL_GENESIS_SEATS - (stats.verified_hotels || 0)) : null;

  return (
    <div className="terminal-theme min-h-screen bg-[#0A0A0A] text-zinc-100" data-testid="bio-landing-page">
      {/* ──────────────────────────── Hero ──────────────────────────── */}
      <section className="px-5 sm:px-10 pt-8 pb-12 border-b border-zinc-800/60" data-testid="bio-hero">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-6 text-xs font-mono text-zinc-500">
            <Terminal className="w-3.5 h-3.5 text-amber-400" />
            <span>~/getbitsy.ai/bio</span>
            <Circle className="w-1.5 h-1.5 fill-emerald-500 text-emerald-500 ml-2 animate-pulse" />
            <span className="text-emerald-400">protocol_online</span>
          </div>

          <h1 className="font-mono text-3xl sm:text-5xl lg:text-6xl font-bold text-zinc-50 leading-[1.05] mb-2 tracking-tight">
            <span className="text-amber-400"># </span>getbitsy.ai
          </h1>
          <h2 className="font-mono text-base sm:text-lg text-zinc-400 mb-6">
            <span className="text-zinc-600">## </span>Chief of Staff for independent hotels.
          </h2>

          <div className="font-mono text-sm sm:text-base text-zinc-400 leading-relaxed mb-8 space-y-1" data-testid="bio-hero-lines">
            {HERO_LINES.map((line, i) => (
              <div key={i} className="text-zinc-300">{line}</div>
            ))}
            <div className="text-zinc-500 pt-2">
              5% platform fee. 1% to a protocol reward pool. No middlemen sitting between you and your guest.
            </div>
          </div>

          <div className="flex flex-wrap gap-3" data-testid="bio-hero-cta">
            <a
              href="#genesis-108"
              className="inline-flex items-center gap-2 px-5 py-3 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-mono text-sm font-bold rounded transition-colors"
              data-testid="bio-cta-apply"
            >
              <Sparkles className="w-4 h-4" />
              Apply for Genesis 108
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#calc"
              className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 hover:border-amber-400 hover:text-amber-200 text-zinc-300 font-mono text-sm rounded transition-colors"
              data-testid="bio-cta-calc"
            >
              <Calculator className="w-4 h-4" />
              Run the context-reload calc
            </a>
          </div>
        </div>
      </section>

      {/* ──────────────────────────── Live ticker ──────────────────────────── */}
      {stats && (
        <section className="px-5 sm:px-10 py-4 border-b border-zinc-800/60 bg-[#0d0f14]" data-testid="bio-ticker">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono">
            <span className="text-zinc-500">live ·</span>
            <span><span className="text-zinc-500">hotels</span> <span className="text-amber-300">{stats.total_hotels}</span></span>
            <span><span className="text-zinc-500">verified</span> <span className="text-emerald-300">{stats.verified_hotels}</span></span>
            <span><span className="text-zinc-500">countries</span> <span className="text-zinc-200">{stats.countries_count}</span></span>
            <span><span className="text-zinc-500">SBTs</span> <span className="text-cyan-300">{stats.total_sbts}</span></span>
            <span><span className="text-zinc-500">bookings</span> <span className="text-zinc-200">{stats.total_bookings}</span></span>
            <span><span className="text-zinc-500">guest_rewards_distributed</span> <span className="text-amber-300">{stats.total_guest_rewards}</span></span>
            <span><span className="text-zinc-500">era</span> <span className="text-emerald-300">{stats.current_era}</span></span>
          </div>
        </section>
      )}

      {/* ──────────────────────────── Context Reload Calc ──────────────────────────── */}
      <section id="calc" className="px-5 sm:px-10 py-16 border-b border-zinc-800/60" data-testid="bio-calc-section">
        <div className="max-w-5xl mx-auto">
          <SectionHeader path="./calc/context-reload" subtitle="The Context Reload Tax — what middleman amnesia actually costs you." />

          <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-3xl font-mono">
            Every time a hotelier re-explains their business from scratch — to a new GM, a new OTA rep, a new
            agency, a new chatbot vendor — they pay a tax. Most hoteliers don't see it because it's amortised
            across the year. We measure it.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-6">
            {/* inputs */}
            <Card className="bg-[#11141a] border-zinc-800" data-testid="bio-calc-inputs">
              <CardContent className="p-5 space-y-5">
                <CalcSlider
                  label="rooms"
                  value={rooms} min={5} max={250} step={5}
                  onChange={setRooms}
                  display={`${rooms} rooms`}
                  testid="calc-rooms"
                />
                <CalcSlider
                  label="annual_staff_turnover_%"
                  value={turnoverPct} min={5} max={120} step={5}
                  onChange={setTurnoverPct}
                  display={`${turnoverPct}% / year`}
                  testid="calc-turnover"
                />
                <CalcSlider
                  label="ota_and_agency_count"
                  value={otaCount} min={0} max={12} step={1}
                  onChange={setOtaCount}
                  display={`${otaCount} ${otaCount === 1 ? 'partner' : 'partners'}`}
                  testid="calc-ota"
                />
                <div className="pt-2 text-[10px] font-mono text-zinc-600 leading-relaxed">
                  formula = (turnover_events × 24h onboarding) + (ota_count × 16h re-pitch) + (rooms × 0.5h × turnover × 0.6 note-rebuild) · @ $55/h fully-loaded
                </div>
              </CardContent>
            </Card>

            {/* outputs */}
            <Card className="bg-gradient-to-b from-amber-950/20 to-[#11141a] border-amber-500/30" data-testid="bio-calc-output">
              <CardContent className="p-5">
                <div className="text-[10px] font-mono text-amber-300 uppercase tracking-widest mb-1">your annual context-reload tax</div>
                <div className="font-mono text-4xl sm:text-5xl font-bold text-amber-200" data-testid="calc-output-dollar">
                  ${calc.totalDollar.toLocaleString()}
                </div>
                <div className="font-mono text-xs text-zinc-500 mt-1">
                  ≈ <span className="text-zinc-300">{calc.totalHours.toLocaleString()}</span> hours/year burnt re-explaining your business
                </div>

                <div className="my-4 border-t border-zinc-800" />

                <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-widest mb-1">bhermes recovers (agent_kb + sbt portability)</div>
                <div className="font-mono text-2xl font-bold text-emerald-300" data-testid="calc-recovered-dollar">
                  ${calc.recoveredDollar.toLocaleString()}/yr
                </div>
                <div className="font-mono text-xs text-zinc-500 mt-1">
                  ≈ {calc.recovered.toLocaleString()}h saved — context survives turnover, OTAs read the same source of truth as your AI.
                </div>

                <div className="mt-5 text-xs font-mono text-zinc-500 leading-relaxed">
                  <span className="text-amber-300">why →</span> we persist hotel + staff context as plain markdown (the Agent KB) and as on-chain SBT history. New GMs read the same knowledge graph your AI does. OTAs verify you cryptographically instead of re-asking for tax IDs.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ──────────────────────────── Sovereignty Score ──────────────────────────── */}
      <section className="px-5 sm:px-10 py-16 border-b border-zinc-800/60" data-testid="bio-quiz-section">
        <div className="max-w-5xl mx-auto">
          <SectionHeader path="./quiz/sovereignty-score" subtitle="Audit your independence — 7 yes/no questions. Honest answers only." />

          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6">
            <Card className="bg-[#11141a] border-zinc-800" data-testid="bio-quiz-card">
              <CardContent className="p-5">
                <ol className="space-y-3">
                  {SOV_QUESTIONS.map((q, idx) => (
                    <li key={q.id} className="font-mono text-sm" data-testid={`quiz-q-${q.id}`}>
                      <div className="flex items-start gap-2 mb-1.5">
                        <span className="text-zinc-600 mt-0.5">[{idx + 1}]</span>
                        <span className="text-zinc-200">{q.text}</span>
                      </div>
                      <div className="flex gap-2 pl-7">
                        {['yes', 'no'].map(opt => (
                          <button
                            key={opt}
                            onClick={() => setAnswers(a => ({ ...a, [q.id]: opt }))}
                            className={`px-3 py-1 rounded border font-mono text-xs transition-colors ${
                              answers[q.id] === opt
                                ? opt === 'yes' ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200' : 'bg-rose-500/15 border-rose-500/40 text-rose-200'
                                : 'border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
                            }`}
                            data-testid={`quiz-${q.id}-${opt}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </li>
                  ))}
                </ol>
                <Button
                  onClick={submitQuiz}
                  disabled={Object.keys(answers).length < SOV_QUESTIONS.length}
                  className="mt-5 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-mono font-bold disabled:bg-zinc-800 disabled:text-zinc-600"
                  data-testid="quiz-submit-btn"
                >
                  Score me · {Object.keys(answers).length}/{SOV_QUESTIONS.length} answered
                </Button>
              </CardContent>
            </Card>

            {/* score card */}
            <Card className={`border-2 ${
              quizSubmitted
                ? tier.color === 'emerald' ? 'bg-emerald-950/20 border-emerald-500/40'
                : tier.color === 'amber'   ? 'bg-amber-950/20 border-amber-500/40'
                : tier.color === 'orange'  ? 'bg-orange-950/20 border-orange-500/40'
                : 'bg-rose-950/20 border-rose-500/40'
                : 'bg-[#11141a] border-zinc-800'
            }`} data-testid="bio-quiz-score-card">
              <CardContent className="p-5 h-full flex flex-col">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">sovereignty score</div>
                {quizSubmitted ? (
                  <>
                    <div className={`font-mono text-6xl font-bold ${
                      tier.color === 'emerald' ? 'text-emerald-300'
                      : tier.color === 'amber'  ? 'text-amber-300'
                      : tier.color === 'orange' ? 'text-orange-300'
                      : 'text-rose-300'
                    }`} data-testid="quiz-score">
                      {quizScore}
                      <span className="text-2xl text-zinc-600 font-normal">/100</span>
                    </div>
                    <div className={`mt-1 mb-3 font-mono text-sm uppercase tracking-wider ${
                      tier.color === 'emerald' ? 'text-emerald-400'
                      : tier.color === 'amber'  ? 'text-amber-400'
                      : tier.color === 'orange' ? 'text-orange-400'
                      : 'text-rose-400'
                    }`} data-testid="quiz-tier">
                      tier · {tier.label}
                    </div>
                    <p className="font-mono text-xs text-zinc-300 leading-relaxed flex-1">{tier.blurb}</p>
                    <a
                      href="#genesis-108"
                      className="mt-4 inline-flex items-center gap-1 text-xs font-mono text-amber-300 hover:text-amber-200"
                      data-testid="quiz-cta-genesis"
                    >
                      Claim a Genesis 108 seat <ArrowRight className="w-3 h-3" />
                    </a>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-center font-mono text-xs text-zinc-600">
                    Answer all 7 to reveal your score.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ──────────────────────────── Memvid Sovereign Memory ──────────────────────────── */}
      <section className="px-5 sm:px-10 py-16 border-b border-zinc-800/60" data-testid="bio-memvid-section">
        <div className="max-w-5xl mx-auto">
          <SectionHeader path="./memory/memvid" subtitle="Sovereign memory. Each hotel owns its own .mp4 — vector-searchable, GDPR-deletable, portable forever." />

          <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-3xl font-mono">
            Most AI vendors hold your guest data hostage in their vector DB.
            We don't. Every hotel on BHERMES gets a per-tenant{' '}
            <code className="bg-zinc-800/60 text-amber-300 px-1 rounded">.mp4</code> file + FAISS index that lives on
            our infra <span className="text-zinc-200">and yours</span>. Replace us tomorrow? Download the MP4. Done.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="bio-memvid-stats">
            <MemvidStat
              label="hotels_with_memory"
              value={memvid?.hotels_with_memory ?? '—'}
              Icon={Database}
              color="amber"
            />
            <MemvidStat
              label="provider"
              value={memvid?.provider ?? 'memvid_mp4'}
              Icon={FileText}
              color="cyan"
            />
            <MemvidStat
              label="compile_cadence"
              value="30 min"
              Icon={Terminal}
              color="emerald"
            />
            <MemvidStat
              label="vendor_lock_in"
              value="zero"
              Icon={Lock}
              color="violet"
            />
          </div>

          <Card className="bg-[#0e1117] border-zinc-800" data-testid="bio-memvid-bullets">
            <CardContent className="p-5">
              <ul className="space-y-2 font-mono text-sm text-zinc-300">
                <li className="flex items-start gap-3">
                  <span className="text-amber-400 mt-0.5">→</span>
                  <span><span className="text-zinc-100">Real vector search</span>, not keyword grep — FAISS index per hotel, MP4-encoded embeddings for portability.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-400 mt-0.5">→</span>
                  <span><span className="text-zinc-100">Sovereign by design</span> — one file per hotel at <code className="bg-zinc-800/60 px-1 rounded text-zinc-400">/data/memvid/&lt;hotel_id&gt;/</code>. No shared multi-tenant blob.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-400 mt-0.5">→</span>
                  <span><span className="text-zinc-100">GDPR-native</span> — `unlink(hotel.mp4)` = the right-to-be-forgotten is one filesystem call. Audit-trail proves it.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-400 mt-0.5">→</span>
                  <span><span className="text-zinc-100">Take your memory with you</span> — every hotel can{' '}
                    <code className="bg-zinc-800/60 px-1 rounded text-amber-300">GET /api/metaverse/bitsy/download/&lt;hotel_id&gt;</code>.
                    If you leave us, you leave with the brain you built.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-400 mt-0.5">→</span>
                  <span><span className="text-zinc-100">Zero infra tax</span> — no separate ChromaDB / Pinecone / Qdrant cluster to babysit. MongoDB write buffer → MP4 compile every 30 min.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {memvid?.hotels?.length > 0 && (
            <div className="mt-4 text-[10px] font-mono text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="bio-memvid-hotels-line">
              <Hash className="w-3 h-3" /> live ·{' '}
              {memvid.hotels.slice(0, 4).map((h, i) => (
                <span key={h.hotel_id}>
                  {h.hotel_id}
                  <span className="text-zinc-600"> ({h.mp4_files} mp4{h.mp4_files === 1 ? '' : 's'})</span>
                  {i < Math.min(memvid.hotels.length, 4) - 1 ? <span className="text-zinc-700"> · </span> : null}
                </span>
              ))}
              {memvid.hotels.length > 4 && <span className="text-zinc-600">+ {memvid.hotels.length - 4} more</span>}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs font-mono">
            <span className="text-zinc-500">
              compare ·{' '}
              <span className="text-zinc-300">PAI (markdown wiki)</span> ✗ ·{' '}
              <span className="text-zinc-300">ChromaDB / Pinecone</span> = vendor holds your data ·{' '}
              <span className="text-amber-300">Memvid = your hotel's data on your hotel's file</span>
            </span>
          </div>
        </div>
      </section>

      {/* ──────────────────────────── Public Verify Proof ──────────────────────────── */}
      <section className="px-5 sm:px-10 py-16 border-b border-zinc-800/60" data-testid="bio-verify-section">
        <div className="max-w-5xl mx-auto">
          <SectionHeader path="./verify/hotel/*" subtitle="Proof of decency. Anyone can audit any hotel in our registry — without phoning a sales rep." />

          <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-3xl font-mono">
            Every verified hotel gets a public, zero-PII trust card. On-chain signer, 30-day attestation activity,
            SBT tier — all readable by your partners, your insurance, your venue door staff. Click any card.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="bio-verify-grid">
            {hotels.map((h) => (
              <button
                key={h.id}
                onClick={() => nav(`/verify/hotel/${h.id}`)}
                className="text-left border border-zinc-800 hover:border-emerald-500/40 bg-[#0e1117] hover:bg-[#11141a] rounded p-4 transition-colors group"
                data-testid={`bio-verify-card-${h.id}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" />
                  verify · trust card
                </div>
                <div className="font-mono text-base text-zinc-100 font-bold mb-1 truncate">{h.name}</div>
                <div className="font-mono text-[11px] text-zinc-500 mb-3">
                  {h.city || '—'}{h.country ? ` · ${h.country}` : ''}
                </div>
                <div className="flex items-center justify-between">
                  <Badge className={`font-mono text-[10px] ${
                    (h.trust_score || 0) >= 80 ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                    : (h.trust_score || 0) >= 50 ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                    : 'bg-zinc-700/30 text-zinc-400 border-zinc-700'
                  } border`}>
                    trust {h.trust_score ?? 0}/100
                  </Badge>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
            ))}
            {hotels.length === 0 && (
              <div className="md:col-span-3 text-center font-mono text-xs text-zinc-600 py-6">
                loading public registry…
              </div>
            )}
          </div>

          <div className="mt-5 text-[10px] font-mono text-zinc-500">
            registry · base-sepolia ·{' '}
            <a
              href="https://sepolia.basescan.org/address/0x940c3717d000cA737bdA8551efA957cbb9333185"
              target="_blank" rel="noopener noreferrer"
              className="text-cyan-400 hover:underline"
              data-testid="bio-verify-basescan-link"
            >
              0x940c3717…3185 on BaseScan ↗
            </a>
          </div>
        </div>
      </section>

      {/* ──────────────────────────── Genesis 108 CTA ──────────────────────────── */}
      <section id="genesis-108" className="px-5 sm:px-10 py-20 bg-gradient-to-b from-[#0A0A0A] via-amber-950/10 to-[#0A0A0A]" data-testid="bio-genesis-section">
        <div className="max-w-4xl mx-auto">
          <SectionHeader path="./genesis-108" subtitle="The first 108 verified hotels mint Era 1: 100,000 HERMES each. After 108 → halves. After 216 → halves again. Halving is mathematical, not negotiable." />

          <Card className="bg-[#11141a] border-amber-500/30 border-2 overflow-hidden" data-testid="bio-genesis-card">
            <CardContent className="p-6 sm:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div data-testid="bio-genesis-seats">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">seats remaining</div>
                  <div className="font-mono text-5xl font-bold text-amber-300">
                    {seatsRemaining ?? '…'}
                    <span className="text-xl text-zinc-600 font-normal"> / 108</span>
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-1">
                    {stats?.verified_hotels ?? '…'} hotels already verified
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">era 1 allocation</div>
                  <div className="font-mono text-5xl font-bold text-emerald-300">100K</div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-1">HERMES per hotel · 50% staked / 29% ops / 20% guest rewards / 1% buffer</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">commission model</div>
                  <div className="font-mono text-5xl font-bold text-cyan-300">5+1<span className="text-2xl">%</span></div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-1">5% platform · 1% protocol reward pool · vs 15–25% OTAs</div>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-5 mb-5 font-mono text-sm text-zinc-300 leading-relaxed">
                <span className="text-amber-300"># what you get:</span> Bitsy (concierge), Marina (ops), Sage (revenue), Argus (verification),
                a HOTEL Reputation SBT, your own on-chain signer key, a public trust card at <span className="text-zinc-100">/verify/hotel/&lt;your_id&gt;</span>,
                and 100,000 HERMES across staking / treasury / guest-rewards from day one.
              </div>
              <div className="font-mono text-sm text-zinc-300 leading-relaxed mb-7">
                <span className="text-amber-300"># what we ask:</span> you're a verifiable independent operator (not a chain),
                you'll pilot the M.O.A.T hardware (IoT shrinkage proofs) or run the AlloggiatiWeb / SES compliance flow,
                and you're ready to ship inside 30 days.
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:hello@getbitsy.ai?subject=Genesis%20108%20application&body=Hotel%20name%3A%0ARooms%3A%0ACity%2Fcountry%3A%0AWebsite%3A%0AContact%20phone%3A%0AWhy%20us%3A"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-mono text-sm font-bold rounded transition-colors"
                  data-testid="bio-genesis-apply-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Apply via email
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register-hotel"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 hover:border-amber-400 hover:text-amber-200 text-zinc-300 font-mono text-sm rounded transition-colors"
                  data-testid="bio-genesis-onboard-btn"
                >
                  <Terminal className="w-4 h-4" />
                  Or start with Argus (AI onboarding)
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ──────────────────────────── Footer ──────────────────────────── */}
      <footer className="px-5 sm:px-10 py-10 border-t border-zinc-800/60" data-testid="bio-footer">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs font-mono text-zinc-500">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>getbitsy.ai</span>
            <span className="text-zinc-700">·</span>
            <span>BHERMES Protocol</span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <Link to="/" className="hover:text-amber-300" data-testid="bio-footer-home">home</Link>
            <Link to="/docs" className="hover:text-amber-300" data-testid="bio-footer-docs">docs</Link>
            <Link to="/why-getbitsy" className="hover:text-amber-300" data-testid="bio-footer-why">why us</Link>
            <a
              href="/BHERMES_PCBWAY_TECH_PACK.pdf"
              target="_blank" rel="noopener noreferrer"
              className="hover:text-amber-300"
              data-testid="bio-footer-pcbway"
            >
              hardware spec ↗
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────
function SectionHeader({ path, subtitle }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        <FileText className="w-3 h-3" />
        path
      </div>
      <h2 className="font-mono text-xl sm:text-2xl text-zinc-100 mb-1">
        <span className="text-amber-400">## </span>{path}
      </h2>
      {subtitle && (
        <p className="font-mono text-sm text-zinc-500 leading-relaxed max-w-3xl">{subtitle}</p>
      )}
    </div>
  );
}

function CalcSlider({ label, value, onChange, min, max, step, display, testid }) {
  return (
    <div data-testid={`bio-${testid}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
        <span className="font-mono text-sm text-amber-200">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-amber-400"
        data-testid={`bio-${testid}-input`}
      />
    </div>
  );
}

function MemvidStat({ label, value, Icon, color }) {
  const palette = {
    amber:   'border-amber-500/30 bg-amber-500/5 text-amber-200',
    cyan:    'border-cyan-500/30 bg-cyan-500/5 text-cyan-200',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200',
    violet:  'border-violet-500/30 bg-violet-500/5 text-violet-200',
  };
  const c = palette[color] || palette.amber;
  return (
    <div className={`border rounded p-3 ${c}`} data-testid={`bio-memvid-stat-${label}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="font-mono text-lg font-bold truncate">{value}</div>
    </div>
  );
}
